import { useState } from "react";
import { toast } from "sonner";
import { Loader2, Package, CheckCircle2 } from "lucide-react";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { BRAND } from "@/lib/brand";

const WAREHOUSE = BRAND.heroFleet;

export default function ShipWithXD() {
  const empty = { company: "", contact: "", email: "", phone: "", origin: "", destination: "", pickup_date: "", delivery_requirements: "", commodity: "", weight: "", length: "", width: "", height: "", trailer_type: "", special_instructions: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const payload = { ...form, weight: form.weight ? parseFloat(form.weight) : null, length: form.length ? parseFloat(form.length) : null, width: form.width ? parseFloat(form.width) : null, height: form.height ? parseFloat(form.height) : null };
      const { data } = await api.post("/quotes", payload);
      setDone(data.quote);
      toast.success("Quote request submitted to XD");
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  if (done) return (
    <PublicLayout>
      <section className="max-w-xl mx-auto px-6 py-32 text-center">
        <span className="grid place-items-center w-16 h-16 rounded-full bg-xd-green/15 text-xd-green mx-auto"><CheckCircle2 size={36} /></span>
        <h1 className="mt-6 font-heading text-3xl font-black">Quote request received</h1>
        <p className="mt-3 text-xd-muted">Reference <span className="font-mono text-white">{done.xd_ref}</span>. The XD team will review your shipment and respond directly — no automated brokering.</p>
        <Button onClick={() => { setDone(null); setForm(empty); }} className="mt-8 bg-xd-orange hover:bg-xd-orangeH text-white">Submit another</Button>
      </section>
    </PublicLayout>
  );

  return (
    <PublicLayout>
      <section className="relative py-16 grain border-b border-xd-border">
        <img src={WAREHOUSE} alt="Warehouse" className="absolute inset-0 w-full h-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-t from-xd-bg to-transparent" />
        <div className="relative max-w-3xl mx-auto px-6 text-center">
          <span className="grid place-items-center w-12 h-12 rounded-lg bg-xd-orange/20 text-xd-orange mx-auto"><Package size={24} /></span>
          <h1 className="mt-4 font-heading text-4xl sm:text-5xl font-black tracking-tight">Ship With XD</h1>
          <p className="mt-3 text-xd-muted">Tell us about your shipment and get a direct quote from the XD team.</p>
        </div>
      </section>

      <form onSubmit={submit} className="max-w-3xl mx-auto px-6 py-12 space-y-8">
        <Group title="Contact">
          <Field label="Company" testid="q-company" value={form.company} onChange={set("company")} required />
          <Field label="Contact name" testid="q-contact" value={form.contact} onChange={set("contact")} required />
          <Field label="Email" type="email" testid="q-email" value={form.email} onChange={set("email")} required />
          <Field label="Phone" testid="q-phone" value={form.phone} onChange={set("phone")} required />
        </Group>
        <Group title="Route">
          <Field label="Pickup location" testid="q-origin" value={form.origin} onChange={set("origin")} required />
          <Field label="Delivery location" testid="q-destination" value={form.destination} onChange={set("destination")} required />
          <Field label="Pickup date" type="date" testid="q-pickup" value={form.pickup_date} onChange={set("pickup_date")} />
          <Field label="Delivery requirements" testid="q-delivery-req" value={form.delivery_requirements} onChange={set("delivery_requirements")} />
        </Group>
        <Group title="Freight">
          <Field label="Commodity" testid="q-commodity" value={form.commodity} onChange={set("commodity")} required />
          <Field label="Weight (lbs)" type="number" testid="q-weight" value={form.weight} onChange={set("weight")} />
          <Field label="Trailer required" testid="q-trailer" value={form.trailer_type} onChange={set("trailer_type")} placeholder="Gooseneck, Flatbed…" />
          <div className="grid grid-cols-3 gap-3 sm:col-span-2">
            <Field label="Length (ft)" type="number" testid="q-length" value={form.length} onChange={set("length")} />
            <Field label="Width (ft)" type="number" testid="q-width" value={form.width} onChange={set("width")} />
            <Field label="Height (ft)" type="number" testid="q-height" value={form.height} onChange={set("height")} />
          </div>
        </Group>
        <div>
          <Label className="text-xd-muted">Special instructions</Label>
          <Textarea data-testid="q-instructions" value={form.special_instructions} onChange={set("special_instructions")} className="mt-1.5 bg-xd-surface border-xd-border" />
        </div>
        <Button data-testid="q-submit" type="submit" disabled={loading} className="w-full h-12 bg-xd-orange hover:bg-xd-orangeH text-white font-bold">
          {loading ? <Loader2 className="animate-spin" size={18} /> : "Get My Quote"}
        </Button>
        <p className="text-xs text-xd-dim text-center">XD holds property carrier authority only. Requests are reviewed by our team — no automated carrier matching.</p>
      </form>
    </PublicLayout>
  );
}

function Group({ title, children }) {
  return (
    <div>
      <h2 className="font-heading text-xl font-bold mb-4">{title}</h2>
      <div className="grid gap-4 sm:grid-cols-2">{children}</div>
    </div>
  );
}
function Field({ label, testid, ...props }) {
  return (
    <div>
      <Label className="text-xd-muted text-sm">{label}</Label>
      <Input data-testid={testid} {...props} className="mt-1.5 bg-xd-surface border-xd-border h-11" />
    </div>
  );
}
